import { FC, useEffect, useState } from 'react';
import { CustomButton, CustomInput, CustomModal } from 'components';
import { useCreateTagMutation } from '../api';
import { useAppSelector } from 'hooks/typedHooks';
import { selectCurrentTeam } from 'containers/app/appSlice';
import { CrossBoundaryIcon } from 'assets/icons';
import ColorPicker from 'components/color-picker/ColorPicker';
import IconPicker from 'components/icon-picker/IconPicker';
import TagPreview from '../create-tag/TagPreview';
import { ButtonType } from 'components/custom-button/CustomButton';
import { Tag } from '../tags.model';
import { selectNextDisplayOrder } from '../tagsSlice';
import DisplayTagOrder from '../create-tag/DisplayTagOrder';

interface CreateTagModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreate: (tag: Tag) => void;
}

const TAG_COLORS = ['#6AA6FF', '#4CAF50', '#F44336', '#FFA726', '#42A5F5', '#FF7043', '#5C6BC0', '#37474F', '#9E9E9E'];

const CreateTagModal: FC<CreateTagModalProps> = ({ isOpen, onClose, onCreate }) => {
  const currentTeam = useAppSelector(selectCurrentTeam);
  const nextDisplayOrder = useAppSelector(selectNextDisplayOrder);

  const [name, setName] = useState('');
  const [color, setColor] = useState(TAG_COLORS[0]);
  const [icon, setIcon] = useState('');
  const [displayOrder, setDisplayOrder] = useState(nextDisplayOrder?.toString() || '1');
  const [error, setError] = useState('');

  useEffect(() => {
    setDisplayOrder(nextDisplayOrder?.toString() || '1');
  }, [nextDisplayOrder]);

  const [createTag, { isLoading }] = useCreateTagMutation();

  const handleCreate = async () => {
    if (!name.trim()) {
      setError('Tag name is required');
      return;
    }

    if (name.length > 50) {
      setError('Tag name must be less than 50 characters');
      return;
    }

    try {
      createTag({
        teamId: currentTeam.id,
        body: {
          name: name.trim(),
          color: color.trim().toUpperCase(),
          icon,
          priority: parseInt(displayOrder, 10)
        }
      })
        .unwrap()
        .then(data => {
          onCreate(data?.data);
          onClose();
          setName('');
          setColor(TAG_COLORS[0]);
          setIcon('');
          setDisplayOrder('0');
        });
    } catch (err) {
      // Handle error
    }
  };

  return (
    <CustomModal
      open={isOpen}
      closeModal={onClose}
      className="w-[587px] md:min-w-[587px]"
      borderRadius="rounded-md"
      backgroundColor="bg-white">
      <div className="flex flex-col gap-6 py-6 px-8">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-bold">Create tag</h2>
          <button onClick={onClose} className="text-secondary">
            <CrossBoundaryIcon />
          </button>
        </div>

        <div className="flex flex-col gap-8 border-b border-highlightGray pb-6">
          <CustomInput
            id="create-tag-name"
            label="Tag name"
            labelClassName="text-17px"
            value={name}
            onChange={setName}
            placeholder="Enter tag name"
            error={error}
            isMandatory
            maxLength={50}
            showRemainingChar={true}
          />

          <div>
            <label className="mb-2 block text-17px font-semibold">Tag color</label>
            <ColorPicker color={color} onChange={setColor} customPresetColorsClassName="mt-0 mb-3" showPresetColors />
          </div>
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <IconPicker
                label="Icon"
                selectedIcon={icon}
                onChange={setIcon}
                emojiPickerClassName="h-auto overflow-y-auto max-h-[350px] shadow-sm"
                alignRight
              />
            </div>

            <div className="flex-1">
              <DisplayTagOrder displayOrder={displayOrder} setDisplayOrder={setDisplayOrder} />
            </div>
          </div>
        </div>
        <div className="flex flex-col gap-2.5">
          <label className="text-17px font-semibold">Preview</label>
          <TagPreview name={name} color={color} icon={icon} />
        </div>

        <div className="flex justify-end gap-4">
          <CustomButton id="create-tag-cancel" onClick={onClose} className="w-[100px]" type={ButtonType.SECONDARY}>
            Cancel
          </CustomButton>
          <CustomButton
            id="create-tag-create"
            onClick={handleCreate}
            className="w-[100px]"
            disabled={isLoading}
            isLoading={isLoading}>
            Create
          </CustomButton>
        </div>
      </div>
    </CustomModal>
  );
};

export default CreateTagModal;
